import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import StatusPill from '../../../components/orizon-ui/StatusPill';
import { TextField, NumberField, SelectField, ToggleField, InfoBox, FieldShell } from '../../../components/orizon-ui/FormField';
import { gruCrea, gruAggiorna } from '../../../lib/services/gruService';
import FormModal, { useFormSalva, useSporco } from '../../../components/gru/FormModal';
import SezioneConfig, { colonnaAttivo } from './SezioneConfig';
import { slugify, CHIAVE_VALIDA, vuoto, interoOrdine, TIPI_CAMPO } from '../utils';

const etichettaTipo = (tipo) => TIPI_CAMPO.find((t) => t.value === tipo)?.label ?? tipo;

// ─── Reparti ─────────────────────────────────────────────────────────────────

const FormReparto = ({ record, onClose, onSaved }) => {
  const modifica = Boolean(record);
  const [label, setLabelState] = useState(record?.label ?? '');
  const [key, setKeyState] = useState(record?.key ?? '');
  const [keyToccata, setKeyToccata] = useState(Boolean(record));
  const [ordine, setOrdine] = useState(String(record?.ordine ?? 0));
  const [attivo, setAttivo] = useState(record?.attivo ?? true);
  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();
  const sporco = useSporco({ label, key, ordine, attivo });

  const setLabel = (v) => {
    setLabelState(v);
    pulisci('label');
    if (!modifica && !keyToccata) setKeyState(slugify(v));
  };
  const setKey = (v) => {
    setKeyToccata(true);
    setKeyState(v);
    pulisci('key');
  };

  const invia = async () => {
    const nuovi = {};
    if (vuoto(label)) nuovi.label = 'Obbligatorio';
    if (!modifica) {
      if (vuoto(key)) nuovi.key = 'Obbligatorio';
      else if (!CHIAVE_VALIDA.test(key)) nuovi.key = 'Solo minuscole, numeri e _';
    }
    const n = interoOrdine(ordine);
    if (Number.isNaN(n)) nuovi.ordine = 'Numero intero';
    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    const payload = { label: label.trim(), ordine: n, attivo, ...(modifica ? {} : { key }) };
    await salva(
      () => (modifica ? gruAggiorna('reparti', record.id, payload) : gruCrea('reparti', payload)),
      modifica ? 'Reparto aggiornato.' : 'Reparto creato.',
      onSaved,
    );
  };

  return (
    <FormModal titolo={modifica ? 'Modifica reparto' : 'Nuovo reparto'} etichetta="Reparti · Reparto" larghezza="md" sporco={sporco} salvando={salvando} onClose={onClose} onSubmit={invia}>
      <TextField label="Nome" name="label" value={label} onChange={setLabel} obbligatorio errore={errori.label} autoFocus placeholder="es. MOTORI" />
      <TextField
        label="Chiave" name="key" value={key} onChange={setKey}
        obbligatorio={!modifica} disabilitato={modifica} errore={errori.key} mono
        suggerimento={modifica ? 'La chiave non è modificabile.' : 'Identificativo interno: minuscole, numeri e _. Non si potrà cambiare.'}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <TextField label="Ordine" name="ordine" type="number" value={ordine} onChange={(v) => { setOrdine(v); pulisci('ordine'); }} errore={errori.ordine} />
      </div>
      <ToggleField label="Attivo" sublabel={attivo ? 'Selezionabile' : 'Non selezionabile'} checked={attivo} onChange={setAttivo} />
    </FormModal>
  );
};

const COLONNE_REPARTI = [
  { key: 'ordine', label: 'Ordine', width: 80, align: 'center', render: (r) => <span className="text-[12px] font-bold tabular-nums text-muted-foreground">{r.ordine}</span> },
  { key: 'label', label: 'Nome', width: 170, render: (r) => <span className="text-[13px] font-black uppercase tracking-tight text-foreground/80">{r.label}</span> },
  { key: 'key', label: 'Chiave', width: 130, render: (r) => <span className="font-mono text-[12px] font-bold text-muted-foreground">{r.key}</span> },
  colonnaAttivo,
];

// ─── Campi di un reparto ─────────────────────────────────────────────────────

const nuovaOpzione = () => ({ label: '', key: '', keyToccata: false, esistente: false });

const FormCampo = ({ record, filtri, onClose, onSaved }) => {
  const modifica = Boolean(record);
  const repartoId = filtri?.reparto_id;

  const [label, setLabelState] = useState(record?.label ?? '');
  const [key, setKeyState] = useState(record?.key ?? '');
  const [keyToccata, setKeyToccata] = useState(Boolean(record));
  const [tipo, setTipo] = useState(record?.tipo ?? '');
  const [unita, setUnita] = useState(record?.unita ?? '');
  const [opzioni, setOpzioni] = useState(
    (record?.opzioni ?? []).map((o) => ({ label: o.label, key: o.key, keyToccata: true, esistente: true })),
  );
  const [obbligatorio, setObbligatorio] = useState(record?.obbligatorio ?? false);
  const [ordine, setOrdine] = useState(String(record?.ordine ?? 0));
  const [attivo, setAttivo] = useState(record?.attivo ?? true);
  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();
  const sporco = useSporco({ label, key, tipo, unita, opzioni, obbligatorio, ordine, attivo });

  const setLabel = (v) => {
    setLabelState(v);
    pulisci('label');
    if (!modifica && !keyToccata) setKeyState(slugify(v));
  };
  const setKey = (v) => {
    setKeyToccata(true);
    setKeyState(v);
    pulisci('key');
  };

  const aggiornaOpzione = (indice, campo, valore) => {
    setOpzioni((prec) => prec.map((o, i) => {
      if (i !== indice) return o;
      if (campo === 'label') {
        return { ...o, label: valore, key: !o.esistente && !o.keyToccata ? slugify(valore) : o.key };
      }
      return { ...o, key: valore, keyToccata: true };
    }));
    pulisci('opzioni');
    pulisci(`opzioni.${indice}.label`);
    pulisci(`opzioni.${indice}.key`);
  };

  const rimuoviOpzione = (indice) => {
    setOpzioni((prec) => prec.filter((_, i) => i !== indice));
    impostaErrori({});
  };

  const invia = async () => {
    const nuovi = {};
    if (vuoto(label)) nuovi.label = 'Obbligatorio';
    if (!modifica) {
      if (vuoto(key)) nuovi.key = 'Obbligatorio';
      else if (!CHIAVE_VALIDA.test(key)) nuovi.key = 'Solo minuscole, numeri e _';
    }
    if (vuoto(tipo)) nuovi.tipo = 'Obbligatorio';
    const n = interoOrdine(ordine);
    if (Number.isNaN(n)) nuovi.ordine = 'Numero intero';

    if (tipo === 'selezione') {
      if (opzioni.length === 0) nuovi.opzioni = 'Aggiungi almeno un\'opzione';
      const viste = new Set();
      opzioni.forEach((o, i) => {
        if (vuoto(o.label)) nuovi[`opzioni.${i}.label`] = 'Obbligatorio';
        if (vuoto(o.key)) nuovi[`opzioni.${i}.key`] = 'Obbligatorio';
        else if (!CHIAVE_VALIDA.test(o.key)) nuovi[`opzioni.${i}.key`] = 'Solo minuscole, numeri e _';
        else if (viste.has(o.key)) nuovi[`opzioni.${i}.key`] = 'Chiave duplicata';
        viste.add(o.key);
      });
    }

    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    const payload = {
      label: label.trim(),
      tipo,
      unita: tipo === 'numero' && !vuoto(unita) ? unita.trim() : null,
      opzioni: tipo === 'selezione' ? opzioni.map((o) => ({ key: o.key, label: o.label.trim() })) : null,
      obbligatorio,
      ordine: n,
      attivo,
      ...(modifica ? {} : { key, reparto_id: repartoId }),
    };

    await salva(
      () => (modifica ? gruAggiorna('campi', record.id, payload) : gruCrea('campi', payload)),
      modifica ? 'Campo aggiornato.' : 'Campo creato.',
      onSaved,
    );
  };

  return (
    <FormModal titolo={modifica ? 'Modifica campo' : 'Nuovo campo'} etichetta="Reparti · Campo tecnico" larghezza="lg" sporco={sporco} salvando={salvando} onClose={onClose} onSubmit={invia}>
      <TextField label="Etichetta" name="label" value={label} onChange={setLabel} obbligatorio errore={errori.label} autoFocus placeholder="es. Diametro" />
      <TextField
        label="Chiave" name="key" value={key} onChange={setKey}
        obbligatorio={!modifica} disabilitato={modifica} errore={errori.key} mono
        suggerimento={modifica ? 'La chiave non è modificabile: i valori già salvati restano agganciati ad essa.' : 'Identificativo interno: minuscole, numeri e _. Non si potrà cambiare.'}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <SelectField
          label="Tipo" name="tipo" value={tipo} onChange={(v) => { setTipo(v); pulisci('tipo'); pulisci('opzioni'); }}
          opzioni={TIPI_CAMPO} obbligatorio errore={errori.tipo}
          suggerimento={modifica ? 'Il tipo non si può cambiare se il campo ha già valori.' : undefined}
        />
        {tipo === 'numero' && (
          <TextField label="Unità" name="unita" value={unita} onChange={setUnita} placeholder="es. mm" errore={errori.unita} />
        )}
      </div>

      {tipo === 'selezione' && (
        <div className="flex flex-col gap-3">
          <FieldShell label="Opzioni dell'elenco" name="opzioni" obbligatorio errore={errori.opzioni}>
            <div className="flex flex-col gap-3">
              {opzioni.map((o, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-2xl bg-muted/20 border border-border/30">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1 min-w-0">
                    <TextField label="Etichetta" name={`opzione-label-${i}`} value={o.label} onChange={(v) => aggiornaOpzione(i, 'label', v)} errore={errori[`opzioni.${i}.label`]} obbligatorio />
                    <TextField
                      label="Chiave" name={`opzione-key-${i}`} value={o.key} onChange={(v) => aggiornaOpzione(i, 'key', v)}
                      errore={errori[`opzioni.${i}.key`]} obbligatorio mono disabilitato={o.esistente}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => rimuoviOpzione(i)}
                    aria-label="Rimuovi opzione"
                    title="Rimuovi opzione"
                    className="mt-6 w-9 h-9 shrink-0 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-all"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setOpzioni((prec) => [...prec, nuovaOpzione()])}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl border-2 border-dashed border-border/40 hover:border-primary/40 hover:bg-primary/5 text-muted-foreground/40 hover:text-primary transition-all duration-200 text-[11px] font-black uppercase tracking-widest"
              >
                <Icon name="Plus" size={14} />
                Aggiungi opzione
              </button>
            </div>
          </FieldShell>
          <InfoBox>
            La chiave di un'opzione esistente non si modifica. Un'opzione già usata da qualche dato non può essere rimossa.
          </InfoBox>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <NumberField label="Ordine" name="ordine" value={ordine} onChange={(v) => { setOrdine(v); pulisci('ordine'); }} errore={errori.ordine} step="1" />
      </div>
      <ToggleField label="Obbligatorio" sublabel={obbligatorio ? 'Richiesto negli articoli e nei componenti' : 'Facoltativo'} checked={obbligatorio} onChange={setObbligatorio} />
      <ToggleField label="Attivo" sublabel={attivo ? 'Visibile nei form' : 'Disattivato: non si compila più, i valori esistenti restano'} checked={attivo} onChange={setAttivo} />
    </FormModal>
  );
};

const COLONNE_CAMPI = [
  { key: 'ordine', label: 'Ordine', width: 80, align: 'center', render: (r) => <span className="text-[12px] font-bold tabular-nums text-muted-foreground">{r.ordine}</span> },
  { key: 'label', label: 'Etichetta', width: 170, render: (r) => <span className="text-[13px] font-black uppercase tracking-tight text-foreground/80">{r.label}</span> },
  { key: 'key', label: 'Chiave', width: 140, render: (r) => <span className="font-mono text-[12px] font-bold text-muted-foreground">{r.key}</span> },
  {
    key: 'tipo', label: 'Tipo', width: 160, align: 'center',
    render: (r) => (
      <span className="text-[11px] font-black uppercase tracking-widest text-foreground/70">
        {etichettaTipo(r.tipo)}
        {r.tipo === 'numero' && r.unita ? ` (${r.unita})` : ''}
        {r.tipo === 'selezione' ? ` · ${(r.opzioni || []).length}` : ''}
      </span>
    ),
  },
  {
    key: 'obbligatorio', label: 'Obbl.', width: 100, align: 'center',
    render: (r) => (r.obbligatorio ? <StatusPill tono="warn" label="Sì" /> : <span className="text-[11px] font-bold text-muted-foreground/40">No</span>),
  },
  colonnaAttivo,
];

// ─── Tab: master (reparti) / dettaglio (campi) ───────────────────────────────

const TabRepartiCampi = () => {
  const [selezionato, setSelezionato] = useState(null); // riga reparto

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-8">
      <SezioneConfig
        entita="reparti"
        titolo="Reparti"
        descrizione="Seleziona un reparto per definirne i campi tecnici"
        nuovoLabel="Nuovo reparto"
        colonne={COLONNE_REPARTI}
        ordineIniziale={{ key: 'ordine', direction: 'asc' }}
        FormComponent={FormReparto}
        onRigaClick={setSelezionato}
        rigaSelezionataId={selezionato?.id}
        compatta
        messaggioVuoto="Nessun reparto configurato"
      />

      {selezionato ? (
        <SezioneConfig
          key={selezionato.id}
          entita="campi"
          titolo={`Campi — ${selezionato.label}`}
          descrizione="Definiscono i dati tecnici di articoli e componenti di questo reparto"
          nuovoLabel="Nuovo campo"
          colonne={COLONNE_CAMPI}
          ordineIniziale={{ key: 'ordine', direction: 'asc' }}
          filtri={{ reparto_id: selezionato.id }}
          FormComponent={FormCampo}
          compatta
          messaggioVuoto="Nessun campo definito per questo reparto"
        />
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 py-24 border-2 border-dashed border-border/40 rounded-3xl bg-muted/5">
          <Icon name="Layers" size={40} className="text-muted-foreground/20" />
          <span className="text-sm font-bold text-muted-foreground/40 uppercase tracking-widest text-center px-6">
            Seleziona un reparto per gestirne i campi
          </span>
        </div>
      )}
    </div>
  );
};

export default TabRepartiCampi;
